// @flow

import React, { Component } from 'react';
import { injectStripe, CardElement } from 'react-stripe-elements';
import { connect } from 'react-redux';
import { components, elements, colors } from '@peachjar/components';
import { TextField } from '@material-ui/core';
import { css } from 'react-emotion';
import axios from 'axios';
import { has, get } from 'lodash';
import config from '../../../../../../config';

const { Note } = elements.typography;
const { Spinner } = components.LoadingIndicators;
const { ButtonPrimaryLarge } = components.Buttons;
const { rose, dragon } = colors;

const PEACHJAR_CONTACT_NUMBER = '(858) 997-2117';

const styles = {
  textField: css({
    marginBottom: '1rem',
    width: '100%',
  }),
  cardElement: css({
    marginTop: '0.5rem',
  }),
  footer: css({ marginTop: '1rem' }),
  validator: css`
    display: flex;
    flex-direction: column;
    background-color: ${rose};
    border: 1px solid ${dragon};
    padding: 1rem;
    border-radius: 3px;
    margin-top: 2rem;
  `,
  colorDragon: css`
    color: ${dragon};
  `,
};

type Props = {|
  +formValues: {
    stripeAmount: number,
  },
  +schoolOrDistrictData: {
    schoolName: string,
    districtName: string,
    id: string,
    ncesId: string,
    ncesDistrictId: string,
    discountCode: string,
  },
|};

type State = {|
  isPaymentProcessing: boolean,
  isCardElementComplete: boolean,
  cardholderName: string,
  paymentErrorMessage: string,
|};

class StripeForm extends Component<Props, State> {
  state = {
    cardholderName: '',
    isPaymentProcessing: false,
    isCardElementComplete: false,
    paymentErrorMessage: '',
  };

  handleNameField = e => {
    this.setState({ [e.target.name]: e.target.value });
  };

  handlePaymentLoading = () => {
    this.setState({ isPaymentProcessing: true });
  };

  handleCardElement = e => {
    this.setState({
      isCardElementComplete: e.complete,
    });
  };

  handleSubmit = ev => {
    // We don't want to let default form submission happen here, which would refresh the page.

    ev.preventDefault();

    const {
      schoolName,
      districtName,
      id: registrationId,
      ncesId,
      ncesDistrictId,
      discountCode,
    } = this.props.schoolOrDistrictData;
    const description = `Initial onboarding payment for ${schoolName ||
      districtName}`;

    const paymentInfo = {
      amount: this.props.formValues.stripeAmount,
      customerId: ncesId || ncesDistrictId,
      description,
    };

    if (discountCode) paymentInfo.discountCode = discountCode;

    this.handlePaymentLoading();
    // Within the context of `Elements`, this call to createToken knows which Element to
    // tokenize, since there's only one in this group.
    this.props.stripe
      .createToken({ name: this.state.cardholderName })
      .then(({ token }) => {
        // send token to our backend server to charge or save to a customer
        this.stripeTokenHandler(token, registrationId);
      })
      .catch(err => {
        this.handleStripeErrors(err);
      });
  };

  handleStripeErrors(err) {
    const defaultErrorMessage =
      'Encountered an error while processing the payment. Please try again.';

    if (has(err, 'response.data.type')) {
      // if we need more granular error treatment, we can use this switch statement below to fill in the cases
      // switch (err.type) {
      //   case 'StripeCardError':
      //     // A declined card error
      //     err.message; // => e.g. "Your card's expiration year is invalid."
      //     break;
      //   case 'RateLimitError':
      //     // Too many requests made to the API too quickly
      //     break;
      //   case 'StripeInvalidRequestError':
      //     // Invalid parameters were supplied to Stripe's API
      //     break;
      //   case 'StripeAPIError':
      //     // An error occurred internally with Stripe's API
      //     break;
      //   case 'StripeConnectionError':
      //     // Some kind of error occurred during the HTTPS communication
      //     break;
      //   case 'StripeAuthenticationError':
      //     // You probably used an incorrect API key
      //     break;
      //   default:
      //     // Handle any other types of unexpected errors
      //     break;
      // }

      this.setState({
        isPaymentProcessing: false,
        paymentErrorMessage: get(
          err,
          'response.data.message',
          defaultErrorMessage
        ),
      });
    } else {
      this.setState({
        isPaymentProcessing: false,
        paymentErrorMessage: get(err, 'message', defaultErrorMessage),
      });
    }
  }

  stripeTokenHandler(token, registrationId) {
    axios(`${config.REACT_APP_API_BASE_URL}/districts/onboarding/${registrationId}/payment`, {
      method: 'POST',
      data: {
        stripeToken: token,
      },
      headers: {
        'Content-Type': 'application/json',
      },
    }).catch(err => {
      this.handleStripeErrors(err);
    });
  }

  render() {
    const {
      isCardElementComplete,
      cardholderName,
      isPaymentProcessing,
      paymentErrorMessage,
    } = this.state;

    return (
      <form onSubmit={this.handleSubmit}>
        <TextField
          data-testid="field-cardholderName"
          className={styles.textField}
          name="cardholderName"
          label="Cardholder's Name"
          value={this.state.cardholderName}
          onChange={this.handleNameField}
        />

        <CardElement
          className={styles.cardElement}
          style={{
            base: {
              fontSize: '16px',
              fontWeight: '100',
              '::placeholder': {
                color: 'rgba(0,0,0,0.54)',
              },
            },
          }}
          onChange={this.handleCardElement}
        />

        {paymentErrorMessage && (
          <div className={styles.validator}>
            <Note className={styles.colorDragon}>
              <b>Error</b>: {paymentErrorMessage}
            </Note>
            <div className="mt-3">
              <Note className={styles.colorDragon}>
                Please try again or call us at {PEACHJAR_CONTACT_NUMBER} for
                assistance.
              </Note>
            </div>
          </div>
        )}

        <div className={`layout-row layout-align-end-center ${styles.footer}`}>
          <ButtonPrimaryLarge
            data-testid="submit-payByCard"
            type="submit"
            disabled={
              !isCardElementComplete || !cardholderName || isPaymentProcessing
            }
          >
            {isPaymentProcessing && <Spinner />}
            Pay
          </ButtonPrimaryLarge>
        </div>
      </form>
    );
  }
}

const mapStateToProps = state => {
  const form = state.form.onboarding;
  const values = form && form.values;
  const onboarding = state.onboarding;

  return {
    formValues: { ...onboarding, ...values },
  }; // FIXME create selector
};

export default connect(mapStateToProps)(injectStripe(StripeForm));



// WEBPACK FOOTER //
// ./src/Onboarding/Registration/onboarding/containers/Payment/components/StripeForm.jsx